import apiClient from './apiClient';
import { PoolInfo } from './blindboxService';

// Types for liquidity providers
export interface LiquidityProvider {
  id: string;
  address: string;
  amount: number;
  timestamp: string;
  rewards: number;
}

export interface AddLiquidityResult {
  provider: LiquidityProvider;
  poolInfo: PoolInfo;
}

export interface RemoveLiquidityResult {
  withdrawal: {
    address: string;
    amountRequested: number;
    amountReceived: number;
    remainingLiquidity: number;
  };
  poolInfo: PoolInfo;
}

export interface DistributeRewardsResult {
  distribution: {
    success: boolean;
    totalRewardsDistributed?: number;
    message?: string;
  };
  providers: LiquidityProvider[];
  poolInfo: PoolInfo;
}

export interface ClaimRewardsResult {
  claim: {
    address: string;
    claimedAmount: number;
  };
  provider: LiquidityProvider;
  poolInfo: PoolInfo;
}

// Liquidity service
const liquidityService = {
  // Add liquidity
  addLiquidity: async (walletAddress: string, amount: number): Promise<AddLiquidityResult> => {
    try {
      const response = await apiClient.post('/liquidity/add', { walletAddress, amount });
      return response.data.data;
    } catch (error) {
      console.error('Error adding liquidity:', error);
      throw error;
    }
  },

  // Remove liquidity
  removeLiquidity: async (walletAddress: string, amount: number): Promise<RemoveLiquidityResult> => {
    try {
      const response = await apiClient.post('/liquidity/remove', { walletAddress, amount });
      return response.data.data;
    } catch (error) {
      console.error('Error removing liquidity:', error);
      throw error;
    }
  },

  // Get all providers
  getProviders: async (): Promise<{ providers: LiquidityProvider[], poolInfo: PoolInfo }> => {
    try {
      const response = await apiClient.get('/liquidity/providers');
      return response.data.data;
    } catch (error) {
      console.error('Error getting providers:', error);
      throw error;
    }
  },

  // Get a specific provider
  getProvider: async (walletAddress: string): Promise<{ provider: LiquidityProvider, poolInfo: PoolInfo }> => {
    try {
      const response = await apiClient.get(`/liquidity/providers/${walletAddress}`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting provider:', error);
      throw error;
    }
  },

  // Distribute rewards
  distributeRewards: async (): Promise<DistributeRewardsResult> => {
    try {
      const response = await apiClient.post('/liquidity/rewards/distribute');
      return response.data.data;
    } catch (error) {
      console.error('Error distributing rewards:', error);
      throw error;
    }
  },

  // Claim rewards
  claimRewards: async (walletAddress: string): Promise<ClaimRewardsResult> => {
    try {
      const response = await apiClient.post('/liquidity/rewards/claim', { walletAddress });
      return response.data.data;
    } catch (error) {
      console.error('Error claiming rewards:', error);
      throw error;
    }
  }
};

export default liquidityService; 